import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { Grid } from '@mui/material';
import { debounce } from 'lodash';
import {
  clearBillingList,
  clearFinalInvoice,
  getBillingList,
  getFinalInvoice,
  getBillingEmployees,
  clearBillingEmployees,
  getBillSelfEligibleInvoices,
  clearBillSelfEligibleInvoices,
  getBillSelfConsolidatedInvoicesList,
  clearBillSelfConsolidatedInvoicesList,
} from 'redux/actions/BillingAction';
import { getServiceCity } from 'redux/actions/UserServiceCity';
import { fetchCompanyList } from 'redux/actions/companyAction';
import BillingFlowTab from './BillingFlowTab';
import BillingFilter from './billingFilter/BillingFilter';
import {
  billingCompanyCardTabs,
  billingCreditCardTabs,
  billingFilterOptions,
} from 'pages/operation/billing/BillingData';
import './BillingFlow.scss';

const BillingFlow = ({ queryParams, payloadData, handleBillingList }) => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const [billingFilter, setBillingFilter] = useState({
    parentCompanyName: {},
    subCompanyName: {},
    city: {},
    employee: {},
  });
  const isManageInvoice = window.location.pathname.includes('manage-invoice');
  const [tab, setTab] = useState(isManageInvoice ? 'charged' : queryParams?.status || 'all');
  const [page, setPage] = useState(parseInt(queryParams?.page) || 0);
  const [pageSize, setRowsPerPage] = useState(parseInt(queryParams?.pageRowSize) || 10);

  const defaultValues = {
    parentCompany: {
      value: queryParams?.companyId,
      label: queryParams?.companyName,
    },
    cityValue: {
      value: queryParams?.cityId,
      label: queryParams?.cityName,
    },
    employee: {
      value: queryParams?.employeeId,
      label: queryParams?.employeeName,
    },
  };

  useEffect(() => {
    dispatch(getServiceCity());
    dispatch(
      fetchCompanyList(
        { ...payloadData, pageSize: 10 },
        { searchType: 'globalSearch', loader: 'backDropLoader' },
      ),
    );
    if (queryParams?.companyId && queryParams?.billingType === 'billToCreditCard') {
      dispatch(getBillingEmployees(queryParams?.companyId, 1, 100, 'Approved'));
    }
    return () => {
      dispatch(clearBillingList());
      dispatch(clearFinalInvoice());
      dispatch(clearBillingEmployees());
      dispatch(clearBillSelfEligibleInvoices());
      dispatch(clearBillSelfConsolidatedInvoicesList());
    };
  }, []);

  useEffect(() => {
    setBillingFilter((prevState) => ({
      ...prevState,
      parentCompanyName: {
        value: queryParams?.companyId,
        label: queryParams?.companyName,
      },
      city: {
        value: queryParams?.cityId,
        label: queryParams?.cityName,
      },
      employee: {
        value: queryParams?.employeeId,
        label: queryParams?.employeeName,
      },
    }));
    setTab(isManageInvoice ? 'charged' : queryParams?.status || 'all');
  }, [
    queryParams?.companyId,
    queryParams?.cityId,
    queryParams?.employeeId,
    queryParams?.status,
    isManageInvoice,
  ]);

  const locationOptions = useSelector((state) => state?.userServiceCity?.userServiceCity?.data);

  useEffect(() => {
    if (locationOptions?.length === 1) {
      const singleOption = locationOptions[0];
      setBillingFilter((prevState) => ({
        ...prevState,
        city: { value: singleOption.id, label: singleOption.name },
      }));
      setSearchParams({
        ...queryParams,
        cityId: singleOption.id,
        cityName: singleOption.name,
      });
      payloadData['city'] = singleOption.id;
      payloadData['cityName'] = singleOption.name;
    }
  }, [locationOptions]);

  const companyTableData = useSelector((state) => state?.userCompany?.companyList?.body);

  const billingList = useSelector((state) => state?.getBillingList?.billingList?.data);

  const billingListLoader = useSelector((state) => state?.getBillingList?.loading);

  const billingEmployeesData = useSelector((state) => state?.getBillingEmployees?.employees?.body);

  const employeeOption =
    billingEmployeesData?.map((val) => {
      return { value: val?.id, label: val.name };
    }) || [];

  const billSelfEligibleInvoices = useSelector((state) => {
    const eligibleInvoices = state?.getBillSelfEligibleInvoices?.eligibleInvoices;

    if (!eligibleInvoices?.body) {
      return eligibleInvoices;
    }

    const hasCheckboxColumn = eligibleInvoices?.header?.some((column) => column.key === 'check');

    return {
      ...eligibleInvoices,
      header: hasCheckboxColumn
        ? eligibleInvoices.header
        : [{ label: '', key: 'check' }, ...(eligibleInvoices.header || [])],
    };
  });

  const billSelfEligibleLoader = useSelector(
    (state) => state?.getBillSelfEligibleInvoices?.loading,
  );

  const billSelfConsolidatedList = useSelector(
    (state) => state?.getBillSelfConsolidatedInvoicesList?.consolidatedInvoicesList,
  );

  const billSelfConsolidatedListLoader = useSelector(
    (state) => state?.getBillSelfConsolidatedInvoicesList?.loading,
  );

  const subCompanyOption = companyTableData?.map((val) => {
    return { value: val?.id, label: val.name };
  });

  const cityOption =
    locationOptions?.length &&
    locationOptions?.map((val) => {
      return { value: val?.id, label: val.name };
    });

  const finalInvoiceData = useSelector((state) => state?.getFinalInvoice?.getFinalInvoice?.data);

  const finalInvoiceLoader = useSelector((state) => state?.getFinalInvoice?.loading);

  // ---Change Tab---
  const handleTab = (tabValue) => {
    const nextPayload = {
      ...payloadData,
      page: 0,
      pageSize: 10,
      status: tabValue === 'all' ? '' : tabValue,
    };

    setTab(tabValue);
    setPage(0);
    setRowsPerPage(10);

    setSearchParams({
      ...queryParams,
      status: nextPayload.status,
      page: 0,
      pageRowSize: 10,
    });

    handleBillingList(nextPayload);
  };

  const handleFilter = (name, newValue) => {
    setBillingFilter((prevState) => {
      const updatedFilter = { ...prevState };

      if (!newValue?.value) {
        delete updatedFilter[name];
      } else {
        updatedFilter[name] = newValue;
      }

      return updatedFilter;
    });
    if (name === 'parentCompanyName') {
      const newParams = {
        ...queryParams,
        companyId: newValue?.value || '',
        companyName: newValue?.label || '',
      };
      if (queryParams?.billingType === 'billToCreditCard') {
        newParams.employeeId = '';
        newParams.employeeName = '';
        setBillingFilter((prevState) => ({
          ...prevState,
          employee: {},
        }));
        payloadData['employeeId'] = '';
        if (newValue?.value) {
          dispatch(getBillingEmployees(newValue.value, 1, 100, 'Approved'));
        } else {
          dispatch(clearBillingEmployees());
        }
      }
      setSearchParams(newParams);
      payloadData['companyId'] = newValue?.value || '';
      payloadData['companyName'] = newValue?.label || '';
    } else if (name === 'city') {
      setSearchParams({
        ...queryParams,
        cityId: newValue?.value || '',
        cityName: newValue?.label || '',
      });
      payloadData['city'] = newValue?.value || '';
      payloadData['cityName'] = newValue?.label || '';
    } else if (name === 'employee') {
      setSearchParams({
        ...queryParams,
        employeeId: newValue?.value || '',
        employeeName: newValue?.label || '',
      });
      payloadData['employeeId'] = newValue?.value || '';
      payloadData['employeeName'] = newValue?.label || '';
    }

    if (!newValue?.value) {
      if (name === 'parentCompanyName') {
        dispatch(clearBillingList());
        dispatch(clearFinalInvoice());
        dispatch(clearBillingEmployees());
        dispatch(clearBillSelfEligibleInvoices());
        dispatch(clearBillSelfConsolidatedInvoicesList());
      } else if (name === 'employee') {
        dispatch(clearBillSelfEligibleInvoices());
        dispatch(clearBillSelfConsolidatedInvoicesList());
      }
    }
    handleBillingList(payloadData);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setSearchParams({ ...queryParams, page: newPage });

    if (queryParams?.status === 'charged') {
      dispatch(getFinalInvoice({ ...payloadData, page: newPage }));
    } else {
      dispatch(getBillingList({ ...payloadData, page: newPage }));
    }
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value));
    setSearchParams({ ...queryParams, pageRowSize: event.target.value });
    setPage(0);

    if (queryParams?.status === 'charged') {
      dispatch(
        getFinalInvoice({
          ...payloadData,
          page: 0,
          pageSize: parseInt(event.target.value),
        }),
      );
    } else {
      dispatch(
        getBillingList({
          ...payloadData,
          page: 0,
          pageSize: parseInt(event.target.value),
        }),
      );
    }
  };

  const onHandleAutoSuggestion = debounce((event, value, fieldName) => {
    if (event?.type === 'change') {
      if (value && fieldName === 'parentCompanyName') {
        dispatch(
          fetchCompanyList(
            { search: value },
            { searchType: 'globalSearch', loader: 'backDropLoader' },
          ),
        );
      } else {
        dispatch(
          fetchCompanyList(
            { search: '' },
            { searchType: 'globalSearch', loader: 'backDropLoader' },
          ),
        );
      }
    }
  }, 500);

  return (
    <div>
      <Grid container gap={3} flexWrap={'wrap'} direction="row" alignitem="center" className="pt30">
        {billingFilterOptions(
          subCompanyOption,
          subCompanyOption,
          cityOption,
          defaultValues,
          queryParams?.billingType,
          employeeOption,
        ) &&
          billingFilterOptions(
            subCompanyOption,
            subCompanyOption,
            cityOption,
            defaultValues,
            queryParams?.billingType,
            employeeOption,
          )?.map((formData, index) => (
            <Grid item md={3.7} sm={6} xs={12} key={index} className="commonMultiform">
              <BillingFilter
                billingFilterValue={billingFilter}
                setBillingFilter={setBillingFilter}
                handleFilter={handleFilter}
                formData={formData}
                onHandleAutoSuggestion={onHandleAutoSuggestion}
              />
            </Grid>
          ))}
      </Grid>
      <div className="mt10 billingToCompanyTabs">
        <BillingFlowTab
          billingList={
            queryParams?.billingType === 'billToCreditCard'
              ? tab === 'charged'
                ? billSelfConsolidatedList
                : billSelfEligibleInvoices
              : tab === 'charged'
                ? finalInvoiceData
                : billingList
          }
          handleTab={handleTab}
          page={page}
          rowsPerPage={pageSize}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          payloadData={payloadData}
          queryParams={queryParams}
          tab={tab}
          billingListLoader={
            queryParams?.billingType === 'billToCreditCard'
              ? tab === 'charged'
                ? billSelfConsolidatedListLoader
                : billSelfEligibleLoader
              : tab === 'charged'
                ? finalInvoiceLoader
                : billingListLoader
          }
          billingFilter={billingFilter}
          billingTabs={
            isManageInvoice
              ? [{ label: 'Final Invoice', value: 'charged' }]
              : queryParams?.billingType === 'billToCreditCard'
                ? billingCreditCardTabs
                : billingCompanyCardTabs
          }
        />
      </div>
    </div>
  );
};

export default BillingFlow;
